import React from 'react'
// libs
import { useSelector } from 'react-redux'
import { getFormInitialValues, getFormValues, isDirty } from 'redux-form'
// utils
import moment from 'moment'
import { getOr, map, orderBy, pipe } from 'lodash/fp'
import { get, isBoolean, isEqual, isNil } from 'lodash'

type Option = {
  label: string
  value: any
  description?: string
}

type FormValues = Record<string, any>

type UseFormDirtyCountReturn = {
  isDirty: boolean
  dirtyFieldsCount: number
}

export const useFormDirtyCount = (form: string): UseFormDirtyCountReturn => {
  const { formValues, isFormDirty, formInitialValues } = useSelector((state) => ({
    formValues: (getFormValues(form)(state) || {}) as FormValues,
    isFormDirty: isDirty(form)(state),
    formInitialValues: (getFormInitialValues(form)(state) || {}) as FormValues
  }))

  const calculateDirtyFieldsCount = React.useMemo(() => {
    const fieldNames = Object.keys(formValues)

    return fieldNames.reduce((acc, key) => {
      const initialValue = formInitialValues[key]
      const formValue = formValues[key]

      if (!isEqual(initialValue, formValue)) {
        const isDate = moment.isMoment(initialValue) || moment.isMoment(formValue)
        const isArray = Array.isArray(initialValue) || Array.isArray(formValue)
        const isOptionValue = hasObjectValueAndLabel(initialValue) || hasObjectValueAndLabel(formValue)
        const isOptionBoolean = isBoolean(initialValue) || isBoolean(formValue)

        let comparisonFunction: ((a: any, b: any) => boolean) | undefined

        if (isOptionBoolean) {
          comparisonFunction = areBooleansEqual
        }

        if (isDate) {
          comparisonFunction = areDatesEqual
        }

        if (isOptionValue) {
          comparisonFunction = areOptionsEqual
        }

        if (isArray) {
          if (key === 'options' || key === 'membersUserPosition') {
            comparisonFunction = areArrayOfOptionsAbsolutelyEqual
          } else if (isArrayOfPrimitives(initialValue) || isArrayOfPrimitives(formValue)) {
            comparisonFunction = isEqual
          } else if (get(formValue?.[0], 'description')) {
            formValue.map(prepareTagValue)

            if (initialValue) {
              initialValue.map(prepareTagValue)
            }
          } else {
            comparisonFunction = areArrayOfOptionsEqual
          }
        }

        let isUnequal = (comparisonFunction && !comparisonFunction(initialValue, formValue)) || !comparisonFunction

        if (key === 'fields') {
          isUnequal = false
        }

        if (isUnequal) {
          acc += 1
        }
      }

      return acc
    }, 0)
  }, [formValues, formInitialValues])

  return { isDirty: isFormDirty, dirtyFieldsCount: calculateDirtyFieldsCount }
}

// Arrays of plain values (e.g. aiSuggestedPrompts) carry no `value` key, so the option-based
// comparators below would read `undefined` for every item and treat any edit as "unchanged".
const isArrayOfPrimitives = (value: any): boolean =>
  Array.isArray(value) && value.every((item) => isNil(item) || typeof item !== 'object')

const hasObjectValueAndLabel = (object: any): object is Option =>
  object && typeof object === 'object' && 'label' in object && 'value' in object

const areArrayOfOptionsEqual = (initial: Option[] = [], current: Option[] = []): boolean => {
  const initialValue = pipe(orderBy('value', 'asc'), map('value'))(initial)
  const currentValue = pipe(orderBy('value', 'asc'), map('value'))(current)
  return isEqual(initialValue, currentValue)
}

const areArrayOfOptionsAbsolutelyEqual = (initial: Option[] = [], current: Option[] = []): boolean =>
  isEqual(initial, current)

const areOptionsEqual = (initial: Option | null, current: Option | null): boolean =>
  isEqual(getOr(null, 'value', initial), getOr(null, 'value', current))

const areDatesEqual = (initial: any, current: any): boolean => !isNil(initial) && moment(initial).isSame(current, 'day')

const areBooleansEqual = (initial: boolean | null | undefined, current: boolean | null | undefined): boolean =>
  isNil(initial) && !current ? true : isEqual(initial, current)

const prepareTagValue = (option: Option): { title: string; description: string } => ({
  title: option.label,
  description: option.description || ''
})
